
import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { translations, TranslationRecord } from '../locales/translations';

export type Language = 'en' | 'fr';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
}

export const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key: string) => key,
});

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Determine initial language based on the domain
  const getInitialLanguage = (): Language => {
    const hostname = window.location.hostname;
    
    // Check for domain-specific language selection
    if (hostname === 'attaboyintelligenceartificielle.com' || 
        hostname.includes('attaboyintelligenceartificielle')) {
      return 'fr';
    }
    
    // Default to English for attaboyintelligence.com and any other domain
    return 'en';
  };

  const [language, setLanguage] = useState<Language>(getInitialLanguage());

  // Effect to set initial language when the component mounts
  useEffect(() => {
    const initialLang = getInitialLanguage();
    setLanguage(initialLang);
  }, []);

  const translate = (key: string): string => {
    if (!translations[key]) {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
    return translations[key][language];
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: translate }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
